/**
 * Dashboard — campaign hub + party overview + activity feed.
 *
 * Role-adaptive: DM sees full party roster, session controls, lore draft
 * badges. Players see their own character card and a scoped activity feed.
 * World map preview sits in the right column on desktop, stacks below on
 * mobile. Activity feed stretches full-width beneath both columns.
 *
 * Data flow:
 *   - On mount, fetches session status, characters, party, audit, and (DM)
 *     lore drafts via Promise.allSettled.
 *   - Opens a CampaignSocket and listens for session/status broadcasts so
 *     the active-session indicator and connected-players list update live.
 *   - DM can start/end a session via WS actions. The resulting broadcast
 *     updates every connected dashboard.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import {
  AuditEntry,
  AuditPage,
  CharacterBundle,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getDashboardSummary,
  getSessionStatus,
  PartySnapshot,
} from '../lib/sessionApi';
import { getManifest } from '../lib/loreApi';
import { totalHp, combinedLevel } from '../lib/character';
import type { Character } from '../lib/character';
import type { ActiveCondition } from '../lib/runtime';
import type { Wallet } from '../lib/party';
import { CampaignSocket } from '../lib/campaignSocket';
import {
  SessionActions,
  SessionServerTypes,
  type ConnectedPlayer,
  type SessionStatusPayload,
  type LogEntryPayload,
} from '../lib/sessionProtocol';
import {
  RuntimeServerTypes,
  type RuntimePatchPayload,
} from '../lib/runtimeProtocol';
import './Dashboard.css';

// Match the other pages' default map binding so the WS subscribe puts us in
// the same session fan-out group.
const DEFAULT_MAP_ID = 'local-dev';

// ── Helpers ──────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function elapsedSince(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  const hrs = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const remSecs = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (hrs > 0) return `${hrs}:${pad(mins)}:${pad(remSecs)}`;
  return `${mins}:${pad(remSecs)}`;
}

function hpColour(ratio: number): string {
  if (ratio > 0.5) return 'var(--active)';
  if (ratio > 0.25) return 'var(--warning)';
  return 'var(--danger)';
}

function walletSummary(w: Wallet | undefined): string {
  if (!w) return '';
  const parts: string[] = [];
  if (w.pp > 0) parts.push(`${w.pp} pp`);
  if (w.gp > 0) parts.push(`${w.gp} gp`);
  if (w.sp > 0) parts.push(`${w.sp} sp`);
  if (w.cp > 0) parts.push(`${w.cp} cp`);
  return parts.length > 0 ? parts.join(', ') : '0 gp';
}

/** Compact "3/5 hit dice" style summary. */
function hitDiceSummary(spent: number | undefined, profile: Character): string {
  const max = combinedLevel(profile);
  const remaining = max - (spent ?? 0);
  return `${remaining}/${max} HD`;
}

/** Compact spell slot summary: "L1: 2 · L2: 1" showing spent counts. */
function slotSummary(
  spellSlots: Partial<Record<string, { leveled?: Record<string, number>; pactExpended?: number | null }>> | undefined,
): string {
  if (!spellSlots) return '';
  const allExpended: Record<string, number> = {};
  for (const ss of Object.values(spellSlots)) {
    if (!ss?.leveled) continue;
    for (const [lvl, exp] of Object.entries(ss.leveled)) {
      allExpended[lvl] = (allExpended[lvl] ?? 0) + (exp ?? 0);
    }
  }
  const levels = Object.keys(allExpended).filter((l) => allExpended[l] > 0).sort();
  if (levels.length === 0) return '';
  return levels.map((l) => `L${l}: ${allExpended[l]} used`).slice(0, 4).join(' \u00b7 ');
}

function feedIcon(action: string): string {
  if (action.includes('hp') || action.includes('damage') || action.includes('heal')) return '\u2764';
  if (action.includes('roll') || action.includes('dice')) return '\uD83C\uDFB2';
  if (action.includes('condition')) return '\u26A0';
  if (action.includes('slot') || action.includes('spell')) return '\u2728';
  if (action.includes('loot') || action.includes('item') || action.includes('inventory')) return '\uD83C\uDF92';
  if (action.includes('wallet') || action.includes('pool') || action.includes('deposit') || action.includes('withdraw')) return '\uD83D\uDCB0';
  if (action.includes('session/start')) return '\uD83D\uDD6F';
  if (action.includes('session/end')) return '\uD83D\uDD6F';
  if (action.includes('session')) return '\uD83D\uDD6F';
  if (action.includes('inspiration')) return '\u2B50';
  if (action.includes('resource')) return '\u26A1';
  return '\u25CF';
}

/** Maps an audit action string to one of the filter categories. */
type FeedCategory = 'hp' | 'roll' | 'condition' | 'spell' | 'loot' | 'wallet' | 'session' | 'inspiration' | 'resource' | 'other';

function actionCategory(action: string): FeedCategory {
  if (action.includes('hp') || action.includes('hit-dice')) return 'hp';
  if (action.includes('roll') || action.includes('dice')) return 'roll';
  if (action.includes('condition')) return 'condition';
  if (action.includes('slot') || action.includes('spell')) return 'spell';
  if (action.includes('loot') || action.includes('inventory')) return 'loot';
  if (action.includes('party') || action.includes('wallet') || action.includes('pool') || action.includes('deposit') || action.includes('withdraw')) return 'wallet';
  if (action.includes('session/start') || action.includes('session/end')) return 'session';
  if (action.includes('inspiration')) return 'inspiration';
  if (action.includes('resource')) return 'resource';
  return 'other';
}

const CATEGORY_LABELS: Record<FeedCategory, string> = {
  hp: 'HP',
  roll: 'Rolls',
  condition: 'Conditions',
  spell: 'Spells',
  loot: 'Loot',
  wallet: 'Wallet',
  session: 'Session',
  inspiration: 'Inspiration',
  resource: 'Resources',
  other: 'Other',
};

const CATEGORY_ICONS: Record<FeedCategory, string> = {
  hp: '\u2764',
  roll: '\uD83C\uDFB2',
  condition: '\u26A0',
  spell: '\u2728',
  loot: '\uD83C\uDF92',
  wallet: '\uD83D\uDCB0',
  session: '\uD83D\uDD6F',
  inspiration: '\u2B50',
  resource: '\u26A1',
  other: '\u25CF',
};

/**
 * Stable per-character colour from a small palette of muted tones that work
 * against the dark background. We hash the characterId to pick one.
 */
const ACTOR_COLOURS = [
  '#c9a961', // gold
  '#6dbf8b', // green
  '#7db4d8', // blue
  '#d4846a', // terracotta
  '#b89cd6', // lavender
  '#78c5b8', // teal
  '#d6a07a', // amber
  '#9aab78', // olive
];

function actorColour(characterId: string | null | undefined): string {
  if (!characterId) return 'var(--fg-strong)';
  let hash = 0;
  for (let i = 0; i < characterId.length; i++) {
    hash = ((hash << 5) - hash + characterId.charCodeAt(i)) | 0;
  }
  return ACTOR_COLOURS[Math.abs(hash) % ACTOR_COLOURS.length];
}

/** Renders the detail object as a compact key-value list for the DM. */
function DetailPanel({ detail, reason }: { detail?: Record<string, unknown> | null; reason?: string | null }) {
  if (!detail && !reason) return null;
  return (
    <div className="feed-detail">
      {reason && (
        <div className="feed-detail__reason">
          <span className="feed-detail__label">Reason:</span> {reason}
        </div>
      )}
      {detail && Object.keys(detail).length > 0 && (
        <div className="feed-detail__grid">
          {Object.entries(detail).map(([key, val]) => (
            <div key={key} className="feed-detail__row">
              <span className="feed-detail__key">{key}</span>
              <span className="feed-detail__val">
                {typeof val === 'object' && val !== null
                  ? JSON.stringify(val, null, 1)
                  : String(val ?? '\u2014')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function NavCard({
  to,
  icon,
  label,
  hint,
}: {
  to: string;
  icon: string;
  label: string;
  hint: string;
}) {
  return (
    <Link to={to} className="nav-card">
      <span className="nav-card__icon">{icon}</span>
      <span className="nav-card__label">{label}</span>
      <span className="nav-card__hint">{hint}</span>
    </Link>
  );
}

function CharacterCard({
  bundle,
  isSelf,
}: {
  bundle: CharacterBundle;
  isSelf: boolean;
}) {
  const profile = bundle.profile as Character | null;
  const runtime = bundle.runtime;
  if (!profile) return null;

  const maxHp = totalHp(profile);
  const currentHp = runtime?.currentHp ?? maxHp;
  const hpRatio = maxHp > 0 ? Math.max(0, Math.min(1, currentHp / maxHp)) : 1;
  const level = combinedLevel(profile);
  const conditions = (runtime?.conditions ?? []) as ActiveCondition[];
  const inspiration = runtime?.inspiration ?? false;
  const slotsStr = slotSummary(runtime?.spellSlots as Record<string, { leveled?: Record<string, number>; pactExpended?: number | null }> | undefined);

  const initial = profile.name.charAt(0).toUpperCase();
  const classLabel = [profile.sheetA.className, profile.sheetB.className]
    .filter(Boolean)
    .join(' / ');

  return (
    <div className={`char-card${isSelf ? ' char-card--self' : ''}`}>
      <div className="char-card__avatar">{initial}</div>
      <div className="char-card__info">
        <div className="char-card__name">{profile.name}</div>
        <div className="char-card__class">
          Level {level} {classLabel}
        </div>
        {conditions.length > 0 && (
          <div className="char-card__conditions">
            {conditions.map((c, i) => (
              <span
                key={`${c.id}-${i}`}
                className={`cond-chip${c.id === 'exhaustion' ? ' cond-chip--exhaustion' : ''}`}
              >
                {c.id}
                {c.level != null && c.level > 0 ? ` ${c.level}` : ''}
              </span>
            ))}
          </div>
        )}
        {/* Expanded detail on the player's own card */}
        {isSelf && (
          <div className="char-card__detail">
            <span className="char-card__detail-item" title="Hit dice remaining">
              {hitDiceSummary(runtime?.hitDiceSpent as number | undefined, profile)}
            </span>
            {slotsStr && (
              <span className="char-card__detail-item" title="Spell slots expended">
                {slotsStr}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="char-card__stats">
        <span
          className={`char-card__inspiration${inspiration ? ' char-card__inspiration--active' : ''}`}
          title={inspiration ? 'Inspired' : 'No inspiration'}
        >
          {'\u2B50'}
        </span>
        <div className="hp-bar">
          <span className="hp-bar__label">
            {currentHp} / {maxHp}
          </span>
          <div className="hp-bar__track">
            <div
              className="hp-bar__fill"
              style={{
                width: `${hpRatio * 100}%`,
                background: hpColour(hpRatio),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedEntry({
  entry,
  showDetail,
  characters,
}: {
  entry: AuditEntry;
  showDetail: boolean;
  characters: CharacterBundle[];
}) {
  const [expanded, setExpanded] = useState(false);
  const isSessionEvent =
    entry.action === 'session/start' || entry.action === 'session/end';

  if (isSessionEvent) {
    return (
      <div className="feed-entry feed-entry--session">
        {entry.summary}
      </div>
    );
  }

  const hasExpandable = showDetail && (entry.detail || entry.reason);
  const isRoll = entry.action.includes('roll');
  const rollDetail = isRoll && entry.detail
    ? (entry.detail as { expression?: string; results?: number[]; total?: number })
    : null;

  // Resolve character name for actor colouring
  const charName = entry.characterId
    ? characters.find((c) => c.id === entry.characterId)?.profile?.name
    : null;

  return (
    <div
      className={`feed-entry${hasExpandable ? ' feed-entry--expandable' : ''}${expanded ? ' feed-entry--expanded' : ''}`}
      onClick={hasExpandable ? () => setExpanded((e) => !e) : undefined}
    >
      <span className="feed-entry__icon">{feedIcon(entry.action)}</span>
      <div className="feed-entry__body">
        <div className="feed-entry__summary">
          {entry.actorUsername && (
            <span
              className="feed-entry__actor"
              style={{ color: actorColour(entry.characterId) }}
            >
              {entry.actorUsername}
            </span>
          )}{' '}
          {entry.summary}
          {/* Inline roll result */}
          {rollDetail && rollDetail.total != null && (
            <span className="feed-entry__roll">
              {rollDetail.expression && (
                <span className="feed-entry__roll-expr">{rollDetail.expression}</span>
              )}
              {rollDetail.results && rollDetail.results.length > 0 && (
                <span className="feed-entry__roll-dice">
                  [{rollDetail.results.join(', ')}]
                </span>
              )}
              <span className="feed-entry__roll-total">= {rollDetail.total}</span>
            </span>
          )}
        </div>
        {/* Character name tag on DM view */}
        {showDetail && charName && (
          <span
            className="feed-entry__char-tag"
            style={{ borderColor: actorColour(entry.characterId) }}
          >
            {charName}
          </span>
        )}
        {/* Expandable detail panel */}
        {expanded && (
          <DetailPanel detail={entry.detail} reason={entry.reason} />
        )}
      </div>
      <span className="feed-entry__ts">{relativeTime(entry.ts)}</span>
      {hasExpandable && (
        <span className={`feed-entry__chevron${expanded ? ' feed-entry__chevron--open' : ''}`}>
          {'\u25B8'}
        </span>
      )}
    </div>
  );
}

/** DM-only filter bar: event-type toggle chips + character dropdown. */
function FeedFilterBar({
  enabledCategories,
  onToggleCategory,
  characterFilter,
  onCharacterFilter,
  characters,
}: {
  enabledCategories: Set<FeedCategory>;
  onToggleCategory: (cat: FeedCategory) => void;
  characterFilter: string | null;
  onCharacterFilter: (charId: string | null) => void;
  characters: CharacterBundle[];
}) {
  const allCats = Object.keys(CATEGORY_LABELS) as FeedCategory[];
  return (
    <div className="feed-filter-bar">
      <div className="feed-filter-bar__chips">
        {allCats.map((cat) => (
          <button
            key={cat}
            className={`feed-filter-chip${enabledCategories.has(cat) ? ' feed-filter-chip--active' : ''}`}
            onClick={() => onToggleCategory(cat)}
            title={`Toggle ${CATEGORY_LABELS[cat]}`}
          >
            <span className="feed-filter-chip__icon">{CATEGORY_ICONS[cat]}</span>
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>
      {characters.length > 1 && (
        <select
          className="feed-filter-bar__char-select"
          value={characterFilter ?? ''}
          onChange={(e) => onCharacterFilter(e.target.value || null)}
        >
          <option value="">All characters</option>
          {characters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.profile?.name ?? c.id}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function ConnectedPlayersList({ players }: { players: ConnectedPlayer[] }) {
  if (players.length === 0) return null;
  return (
    <div className="dash__connected">
      {players.map((p) => (
        <span
          key={p.sub}
          className="connected-pip"
          title={`${p.username} (${p.role})`}
        >
          <span className="connected-pip__dot" />
          {p.username}
        </span>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const { impersonating } = useIdentity();
  // Suppress DM-only UI (session controls, admin panel, level-up card) when
  // impersonating so the DM sees the dashboard as the player would.
  const admin = !impersonating && isAdmin(user);

  // Data state
  const [characters, setCharacters] = useState<CharacterBundle[]>([]);
  const [party, setParty] = useState<PartySnapshot | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [draftCount, setDraftCount] = useState(0);
  const [loreNewCount, setLoreNewCount] = useState(0);

  // Feed filter state (DM-only, but stored regardless to keep hooks stable)
  const [enabledCategories, setEnabledCategories] = useState<Set<FeedCategory>>(
    () => new Set(Object.keys(CATEGORY_LABELS) as FeedCategory[]),
  );
  const [characterFilter, setCharacterFilter] = useState<string | null>(null);

  const handleToggleCategory = useCallback((cat: FeedCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const [connectedPlayers, setConnectedPlayers] = useState<ConnectedPlayer[]>([]);
  const [sessionActionPending, setSessionActionPending] = useState(false);

  // Animated session timer — ticks every second when a session is live
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sessionActive || !sessionStartedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1_000);
    return () => clearInterval(id);
  }, [sessionActive, sessionStartedAt]);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingMore = useRef(false);

  // WebSocket ref
  const socketRef = useRef<CampaignSocket | null>(null);

  // ── Initial data fetch (single composite call) ──

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const summary = await getDashboardSummary();
        if (cancelled) return;

        setCharacters(summary.characters);
        setParty({ party: summary.party, loot: summary.loot });
        setAuditEntries(summary.audit.entries);
        setNextBefore(summary.audit.nextBefore);
        setSessionActive(summary.status.active);
        setSessionStartedAt(summary.status.startedAt ?? null);
        setConnectedPlayers(summary.status.connectedPlayers as ConnectedPlayer[]);
        setDraftCount(summary.draftCount);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    // Lore "new since last visit" hint — compare the manifest entry count
    // against the count stored in localStorage from the player's last Lore
    // page visit. Non-blocking; failures silently ignored.
    getManifest()
      .then((manifest) => {
        if (cancelled) return;
        const STORAGE_KEY = 'goobdoob:lore:lastSeenCount';
        const lastSeen = parseInt(localStorage.getItem(STORAGE_KEY) ?? '0', 10);
        const current = manifest.entries.length;
        if (current > lastSeen) {
          setLoreNewCount(current - lastSeen);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // ── WebSocket for live session status ──

  useEffect(() => {
    let cs: CampaignSocket | null = null;
    let cancelled = false;

    CampaignSocket.connect({
      sessionId: DEFAULT_SESSION_ID,
      mapId: DEFAULT_MAP_ID,
    })
      .then((socket) => {
        if (cancelled) {
          socket.close();
          return;
        }
        cs = socket;
        socketRef.current = socket;

        // Listen for session status broadcasts
        socket.on<SessionStatusPayload>(
          SessionServerTypes.SessionStatus,
          (payload) => {
            setSessionActive(payload.active);
            setSessionStartedAt(payload.startedAt ?? null);
            setSessionActionPending(false);
            // Re-fetch connected players on status change (a full
            // connected-players broadcast would add complexity; polling
            // the HTTP endpoint on status change is simpler and accurate).
            getSessionStatus()
              .then((s) => setConnectedPlayers(s.connectedPlayers))
              .catch(() => {});
          },
        );

        // Listen for runtime patches to keep the party roster live.
        // Each patch carries the characterId + whichever fields changed;
        // we merge them into our local character bundles so HP bars,
        // conditions, slots etc. update without a page refresh.
        socket.on<RuntimePatchPayload>(
          RuntimeServerTypes.RuntimePatch,
          (patch) => {
            setCharacters((prev) =>
              prev.map((c) => {
                if (c.id !== patch.characterId || !c.runtime) return c;
                const rt = { ...c.runtime };
                if (patch.currentHp != null) rt.currentHp = patch.currentHp;
                if (patch.tempHp != null) rt.tempHp = patch.tempHp;
                if (patch.hitDiceSpent != null) rt.hitDiceSpent = patch.hitDiceSpent;
                if (patch.inspiration != null) rt.inspiration = patch.inspiration;
                if (patch.conditions) rt.conditions = patch.conditions;
                if (patch.spellSlots) rt.spellSlots = patch.spellSlots;
                if (patch.resources) rt.resources = patch.resources;
                if (patch.inventory) rt.inventory = patch.inventory;
                rt.version = patch.version;
                rt.updatedAt = patch.updatedAt;
                return { ...c, runtime: rt };
              }),
            );
          },
        );

        // Listen for live log entries to prepend to the feed
        socket.on<LogEntryPayload>(
          SessionServerTypes.LogEntry,
          (payload) => {
            const entry: AuditEntry = {
              id: payload.id,
              ts: payload.ts,
              sessionId: DEFAULT_SESSION_ID,
              actorSub: '',
              actorUsername: payload.actorUsername,
              action: payload.expression ?? payload.kind,
              version: 0,
              summary: payload.summary,
              reason: payload.reason ?? null,
            };
            setAuditEntries((prev) => [entry, ...prev]);
          },
        );
      })
      .catch(() => {
        // WS connection failure is non-critical for the dashboard — the
        // HTTP read path is the source of truth. The status indicator
        // just won't update live.
      });

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, []);

  // ── Session start / end actions ──

  const handleStartSession = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || sessionActionPending) return;
    setSessionActionPending(true);
    socket.send({ action: SessionActions.Start });
  }, [sessionActionPending]);

  const handleEndSession = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || sessionActionPending) return;
    setSessionActionPending(true);
    socket.send({ action: SessionActions.End });
  }, [sessionActionPending]);

  // ── Load more audit entries ──

  const loadMoreAudit = useCallback(async () => {
    if (!nextBefore || loadingMore.current) return;
    loadingMore.current = true;
    try {
      const page: AuditPage = await getAuditLog(DEFAULT_SESSION_ID, {
        limit: 25,
        before: nextBefore,
      });
      setAuditEntries((prev) => [...prev, ...page.entries]);
      setNextBefore(page.nextBefore);
    } catch {
      // Silently ignore pagination errors
    } finally {
      loadingMore.current = false;
    }
  }, [nextBefore]);

  // ── DM session notes (localStorage for v1, DDB in v2) ──

  const NOTES_KEY = 'goobdoob:session-notes';
  const [sessionNotes, setSessionNotes] = useState(() => {
    try { return localStorage.getItem(NOTES_KEY) ?? ''; }
    catch { return ''; }
  });
  const notesTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setSessionNotes(val);
    // Debounce localStorage write by 500ms
    if (notesTimeout.current) clearTimeout(notesTimeout.current);
    notesTimeout.current = setTimeout(() => {
      try { localStorage.setItem(NOTES_KEY, val); }
      catch { /* quota / private mode */ }
    }, 500);
  }, []);

  // ── Keyboard shortcuts ──

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Ignore when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // S — toggle session start/end (DM only)
      if (e.key === 's' || e.key === 'S') {
        if (!admin) return;
        e.preventDefault();
        if (sessionActive) handleEndSession();
        else handleStartSession();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [admin, sessionActive, handleStartSession, handleEndSession]);

  // ── Derive hints for nav cards ──

  const selfChar = characters.length > 0 ? characters[0] : null;
  const selfName = selfChar?.profile?.name ?? '...';
  const selfHp = selfChar?.runtime?.currentHp ?? '?';
  const selfMaxHp = selfChar?.profile ? totalHp(selfChar.profile as Character) : '?';
  const sheetHint = `${selfName} \u2014 ${selfHp}/${selfMaxHp} HP`;

  const walletHint = party?.party?.wallet
    ? walletSummary(party.party.wallet)
    : 'Party treasury';

  const loreHint =
    admin && draftCount > 0
      ? `${draftCount} unpublished draft${draftCount > 1 ? 's' : ''}`
      : loreNewCount > 0
        ? `${loreNewCount} new note${loreNewCount > 1 ? 's' : ''} since last visit`
        : 'Campaign lore & notes';

  // ── Render ──

  if (loading) {
    return (
      <div className="dash">
        <div className="dash__loading">Loading campaign data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash">
        <div className="dash__loading" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ── Hero ── */}
      <div className="dash__hero">
        <h1 className="dash__title">GoobDoob</h1>
        <span
          className={`dash__session-badge${sessionActive ? ' dash__session-badge--live' : ''}`}
        >
          <span
            className={`dash__session-dot${sessionActive ? ' dash__session-dot--live' : ''}`}
          />
          {sessionActive
            ? <>Session in progress{sessionStartedAt && (
                <span className="dash__session-timer">{elapsedSince(sessionStartedAt)}</span>
              )}</>
            : 'No active session'}
        </span>
      </div>

      {/* ── Left column ── */}
      <div className="dash__left">
        {/* Connected players */}
        <ConnectedPlayersList players={connectedPlayers} />

        {/* Nav cards */}
        <div className="dash__nav-cards">
          <NavCard to="/vtt" icon={'\uD83D\uDDFA'} label="VTT" hint="Open the virtual tabletop" />
          <NavCard to="/sheet" icon={'\uD83D\uDCDC'} label="Sheet" hint={sheetHint} />
          <NavCard to="/party" icon={'\uD83D\uDCB0'} label="Party" hint={walletHint} />
          <NavCard to="/lore" icon={'\uD83D\uDCD6'} label="Lore" hint={loreHint} />
        </div>

        {/* DM tools */}
        {admin && (
          <div className="dash__dm-tools">
            <h2>DM Tools</h2>
            <div className="dm-action-row">
              {sessionActive ? (
                <button
                  onClick={handleEndSession}
                  disabled={sessionActionPending}
                  style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
                >
                  {sessionActionPending ? 'Ending...' : 'End Session'}
                </button>
              ) : (
                <button
                  onClick={handleStartSession}
                  disabled={sessionActionPending}
                  style={{ borderColor: 'var(--active)', color: 'var(--active)' }}
                >
                  {sessionActionPending ? 'Starting...' : 'Start Session'}
                </button>
              )}
              <Link to="/admin" className="dm-draft-badge">
                Admin Panel
              </Link>
              {draftCount > 0 && (
                <Link to="/lore" className="dm-draft-badge">
                  <span className="dm-draft-badge__count">{draftCount}</span>
                  unpublished draft{draftCount > 1 ? 's' : ''}
                </Link>
              )}
              <span className="dash__kbd-hint">
                Press <kbd>S</kbd> to {sessionActive ? 'end' : 'start'} session
              </span>
            </div>
            <textarea
              className="dm-session-notes"
              placeholder="Session plan / agenda..."
              value={sessionNotes}
              onChange={handleNotesChange}
              rows={3}
            />
          </div>
        )}

        {/* Party roster */}
        <div className="dash__roster">
          <h2>Party</h2>
          {characters.length === 0 ? (
            <div className="dash__empty">
              <p>No characters loaded yet.</p>
            </div>
          ) : (
            characters.map((c) => (
              <CharacterCard
                key={c.id}
                bundle={c}
                isSelf={c.id === characters[0]?.id}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right column (map) ── */}
      <div className="dash__right">
        <div className="dash__map-frame">
          <img src="/map.jpg" alt="The World of GoobDoob" loading="lazy" />
          <div className="dash__map-overlay">
            <h3 className="dash__map-title">The World of GoobDoob</h3>
            <p className="dash__map-hint">Click to explore the world map</p>
          </div>
          <a
            href="/map"
            target="_blank"
            rel="noopener noreferrer"
            className="dash__map-link"
          >
            Open full map
          </a>
        </div>

        {admin && (
          <NavCard
            to="/levelup"
            icon={'\u2B06'}
            label="Level Up"
            hint="Manage character progression"
          />
        )}
      </div>

      {/* ── Activity feed ── */}
      <div className="dash__feed">
        <h2>Recent Activity</h2>

        {/* DM-only filter bar */}
        {admin && auditEntries.length > 0 && (
          <FeedFilterBar
            enabledCategories={enabledCategories}
            onToggleCategory={handleToggleCategory}
            characterFilter={characterFilter}
            onCharacterFilter={setCharacterFilter}
            characters={characters}
          />
        )}

        {auditEntries.length === 0 ? (
          <div className="dash__empty">
            <p>No activity yet. Start a session to see the feed come alive.</p>
          </div>
        ) : (
          <div className="feed-list">
            {auditEntries
              .filter((entry) => {
                // Category filter (DM only — players see everything)
                if (admin && !enabledCategories.has(actionCategory(entry.action))) return false;
                // Character filter (DM only)
                if (admin && characterFilter && entry.characterId && entry.characterId !== characterFilter) return false;
                return true;
              })
              .map((entry) => (
                <FeedEntry
                  key={entry.id}
                  entry={entry}
                  showDetail={admin}
                  characters={characters}
                />
              ))}
            {nextBefore && (
              <div className="feed-more">
                <button onClick={loadMoreAudit}>Load older entries</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
